import React from 'react';
import ThemeContext, { ThemeKind, ThemePreference } from './ThemeContext';

const THEME_STORAGE_KEY = 'THEME';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const getStoredPreference = (): ThemePreference => {
    const theme = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeKind | null;
    if (theme && Object.values(ThemeKind).includes(theme)) {
        return theme as ThemeKind;
    }
    return 'system';
};

// No key means 'system', so a page that reads THEME falls back to the OS setting.
const savePreference = (preference: ThemePreference) => {
    if (preference === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
        window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
};

const getSystemTheme = (): ThemeKind =>
    window.matchMedia(DARK_SCHEME_QUERY).matches ? ThemeKind.Dark : ThemeKind.Light;

const otherTheme = (theme: ThemeKind): ThemeKind => (theme === ThemeKind.Dark ? ThemeKind.Light : ThemeKind.Dark);

// Every click changes the theme on screen, except the step back to 'system'.
const nextPreference = (preference: ThemePreference, systemTheme: ThemeKind): ThemePreference => {
    if (preference === 'system') return otherTheme(systemTheme);
    return preference === systemTheme ? 'system' : otherTheme(preference);
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = (props) => {
    const [preference, setPreference] = React.useState<ThemePreference>(getStoredPreference);
    const [systemTheme, setSystemTheme] = React.useState<ThemeKind>(getSystemTheme);
    const theme = preference === 'system' ? systemTheme : preference;

    const cyclePreference = () => {
        const next = nextPreference(preference, systemTheme);
        savePreference(next);
        setPreference(next);
    };

    React.useEffect(() => {
        const query = window.matchMedia(DARK_SCHEME_QUERY);
        const onChange = () => setSystemTheme(getSystemTheme());
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    // A layout effect, so no frame shows the mounted page in the wrong theme.
    React.useLayoutEffect(() => {
        document.documentElement.classList.toggle('dark', theme === ThemeKind.Dark);
        document.documentElement.style.colorScheme = theme;
    }, [theme]);

    return (
        <ThemeContext.Provider
            value={{
                theme,
                preference,
                cyclePreference,
            }}
        >
            {props.children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
